import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import useGNBHandler from "@/components/common/GNB/useGNBHandler";
import useLogout from "@/components/common/GNB/useLogout";
import { trackMyPageDropdownMenuClicked } from "@/components/myPage/utils/myPageMixpanel";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import useWindowDimensions from "@/lib/web-ui/useWindowDimensions";
import { CASH_INDEX_PAGE, EXTERNAL_PARTNER_CENTER, LIBRARIES_INDEX_PAGE, MYPAGE_COUPONS, MYPAGE_DEVICE_INDEX_PAGE, MYPAGE_ORDERS_INDEX_PAGE, MYPAGE_PAGE, MYPAGE_PASSES, SOLVOOK_WRITE_STUDIO } from "@/settings/constant";
import { IconV2, Typography } from "@bookips/sds";
import { useRouter } from "next/router";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDropdownPosition } from "../hooks";
const USER_DROPDOWN_WIDTH = 140;
const GNBUserDropdown = (param)=>{
    let { className  } = param;
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);
    const { isDesktop  } = useWindowDimensions();
    const { labName , isAuthor  } = useGNBHandler();
    const { handleLogout  } = useLogout();
    const router = useRouter();
    const { position: dropdownPosition , updatePosition  } = useDropdownPosition({
        dropdownWidth: USER_DROPDOWN_WIDTH,
        gap: 8
    });
    // ESC 키로 닫기
    useEffect(()=>{
        const handleEscape = (event)=>{
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };
        document.addEventListener("keydown", handleEscape);
        return ()=>document.removeEventListener("keydown", handleEscape);
    }, []);
    const handleItemClick = (item)=>{
        trackMyPageDropdownMenuClicked(item.label);
        if (item.onClick) {
            item.onClick();
        } else if (item.url) {
            if (item.external) {
                window.open(item.url, "_blank", "noopener,noreferrer");
            } else {
                router.push(item.url);
            }
        }
        setIsOpen(false);
    };
    const menuItems = [
        {
            label: "마이 쏠북 홈",
            url: MYPAGE_PAGE,
            icon: IconV2.Home
        },
        ...labName ? [
            {
                label: "브랜드 홈",
                url: "/@".concat(labName),
                icon: IconV2.BrandHome
            }
        ] : [],
        ...isAuthor ? [
            {
                label: "파트너 센터",
                icon: IconV2.PartnerCenter,
                onClick: ()=>window.open(EXTERNAL_PARTNER_CENTER, "_blank", "noopener,noreferrer")
            }
        ] : [],
        {
            label: "자료 보관함",
            url: LIBRARIES_INDEX_PAGE,
            icon: IconV2.OldLibrary
        },
        {
            label: "엑스퍼트",
            url: SOLVOOK_WRITE_STUDIO,
            icon: IconV2.Expert
        },
        {
            label: "캐시/포인트",
            url: CASH_INDEX_PAGE,
            icon: IconV2.Point
        },
        {
            label: "쿠폰 관리",
            url: MYPAGE_COUPONS,
            icon: IconV2.Coupon
        },
        {
            label: "이용권",
            url: MYPAGE_PASSES,
            icon: IconV2.Pass
        },
        {
            label: "주문 내역",
            url: MYPAGE_ORDERS_INDEX_PAGE,
            icon: IconV2.HandoutCabinet
        },
        {
            label: "기기 관리",
            url: MYPAGE_DEVICE_INDEX_PAGE,
            icon: IconV2.Mobile
        },
        {
            label: "로그아웃",
            onClick: handleLogout,
            icon: IconV2.Logout
        }
    ];
    // PC: hover로 열기
    const handlePointerEnter = (e)=>{
        if (e.pointerType === "mouse" && isDesktop && !isOpen) {
            updatePosition(buttonRef.current);
            mixpanelEvent("GNB Button Clicked", {
                "button name": "마이 쏠북",
                "button type": "mypage"
            });
            setIsOpen(true);
        }
    };
    // PC: hover로 닫기
    const handlePointerLeave = (e)=>{
        if (e.pointerType === "mouse" && isDesktop && isOpen) {
            setIsOpen(false);
        }
    };
    const handleToggle = ()=>{
        // PC: 클릭 시 마이 쏠북 페이지로 이동
        if (isDesktop) {
            setIsOpen(false);
            router.push(MYPAGE_PAGE);
            return;
        }
        // 모바일/태블릿: 클릭으로 토글
        if (!isOpen) {
            updatePosition(buttonRef.current);
            mixpanelEvent("GNB Button Clicked", {
                "button name": "마이 쏠북",
                "button type": "mypage"
            });
        }
        setIsOpen(!isOpen);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("relative", className),
        onPointerEnter: handlePointerEnter,
        onPointerLeave: handlePointerLeave,
        children: [
            /*#__PURE__*/ _jsxs("button", {
                ref: buttonRef,
                type: "button",
                onClick: handleToggle,
                "aria-expanded": isOpen,
                "aria-haspopup": "true",
                className: cn("flex min-h-[50px] min-w-[52px] flex-col items-center gap-0.5 rounded-md p-1", "transition-colors hover:bg-gray-50", isOpen && "bg-gray-100"),
                children: [
                    /*#__PURE__*/ _jsx(IconV2.User, {}),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "label",
                        className: "max-lg:hidden",
                        children: "마이 쏠북"
                    })
                ]
            }),
            isOpen && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    !isDesktop && /*#__PURE__*/ _jsx("div", {
                        className: "fixed inset-0 z-[899]",
                        onClick: ()=>setIsOpen(false)
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        role: "menu",
                        className: "fixed z-[900]",
                        style: {
                            top: dropdownPosition.top,
                            left: dropdownPosition.left
                        },
                        onPointerEnter: handlePointerEnter,
                        onPointerLeave: handlePointerLeave,
                        children: [
                            isDesktop && /*#__PURE__*/ _jsx("div", {
                                className: "absolute -top-2 left-0 h-2 w-full"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: cn("w-[140px] py-2", "rounded-md bg-white", "shadow-[0_4px_12px_rgba(0,0,0,0.1)]"),
                                children: menuItems.map((item, index)=>/*#__PURE__*/ _jsx(Fragment, {
                                        children: /*#__PURE__*/ _jsxs("button", {
                                            type: "button",
                                            role: "menuitem",
                                            onClick: ()=>handleItemClick(item),
                                            className: cn("flex w-full items-center gap-2 px-4 py-2.5", "transition-colors hover:bg-gray-50"),
                                            children: [
                                                item.icon && /*#__PURE__*/ _jsx(item.icon, {
                                                    size: "xs"
                                                }),
                                                /*#__PURE__*/ _jsx(Typography, {
                                                    variant: "body2",
                                                    color: "primary",
                                                    children: item.label
                                                })
                                            ]
                                        })
                                    }, index))
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default GNBUserDropdown;
